// Namespaced, human-readable uid minting: `<prefix>.<slug>.<n>` (e.g.
// "reward.goldperturn.0", "techpassive.clear-fog-bonus.0"). <slug> is the
// typed id lowercased with whitespace collapsed to '-'; <n> increments until
// the uid is unused. The namespace prefix makes collisions with entity ids
// structurally impossible, so callers only need to pass the uids of the
// metadata collections sharing the namespace.
//
// Note the uid is a REFERENCE KEY — tech node unlocks, building reward
// levels, resource attribute lists, localization entries and images are all
// keyed by it. Renaming an instance later only changes its display text; the
// uid keeps the original slug, and changing a uid means rewriting every
// reference to it.
export function mintNamespacedUid(prefix: string, id: string, takenUids: Iterable<string>): string {
    const slug = id.trim().toLowerCase().replace(/\s+/g, '-');
    const taken = new Set(takenUids);

    let n = 0;
    while (taken.has(`${prefix}.${slug}.${n}`)) {
        n++;
    }
    return `${prefix}.${slug}.${n}`;
}

// Entity-style ids: `<kind>.<camelSlug>` derived from the prompted display
// name — e.g. ('unit', 'War Elephant') → "unit.warElephant", matching the
// existing entity id style. Unlike metadata uids there is no counter
// segment; collisions append 2, 3, … (unit.warElephant2). Same reference-key
// caveat as mintNamespacedUid: renaming the entity later changes its display
// name only, the id keeps the original slug.
export function mintEntityId(kind: string, name: string, takenIds: Iterable<string>): string {
    const words = name.trim().split(/\s+/)
        .map(w => w.replace(/[^a-zA-Z0-9]/g, ''))
        .filter(w => w.length > 0);
    const slug = words.length === 0
        ? 'unnamed'
        : words[0].toLowerCase() + words.slice(1)
            .map(w => w[0].toUpperCase() + w.slice(1).toLowerCase())
            .join('');

    const taken = new Set(takenIds);
    let candidate = `${kind}.${slug}`;
    for (let n = 2; taken.has(candidate); n++) {
        candidate = `${kind}.${slug}${n}`;
    }
    return candidate;
}
